import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import { useParams, Link as ReactLink, useLocation, useNavigate } from 'react-router-dom'

import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'

import { pirStepperArray } from '../../util/pirConsts.js'
import { logo } from '../../images/images.js'
import { formatPIRForOrderSummary, formatPIRForForm } from '../../util/pirFormatting'
import { getPaymentIntentSecret, getPIRById, postUploadPIRFile } from '../../dataClient/apiCalls'
import {
  CenteredRowContainerReverse,
  LightBGContainer,
  FlexColumn,
  Logo,
  PageWithTitle,
  Header,
  SpaceBetweenRowContainer
} from '../styles/Containers'
import { PageTitle, SmallBodyText } from '../styles/Text'
import OrderSummary from './OrderSummary'
import PaymentForm from './PaymentForm'
import Loading from '../common/Loading'
import LoadingError from '../common/LoadingError'
import Button, { SecondaryButton } from '../common/Button'
import PaymentConfirmation from './PaymentConfirmation'
import Link from '../styles/Link'
import Carousel from '../common/Carousel.js'
import pirTerminology from '../../util/pirTerminology'

const FixedContainer = styled.div`
  position: fixed;
  left: 5%;
  top: 50px;
`

const SectionContainer = styled(LightBGContainer)`
  padding: 40px;
  height: max-content;
  width: 50%;

  @media only screen and (max-width: 1000px) {
    padding: 25px;
    width: 90%;
  }
`

const BackButton = styled(SecondaryButton)`
  @media only screen and (max-width: 600px) {
    order: 1;
  }
`

function PaymentPage() {
  const navigate = useNavigate()
  const { pirId } = useParams()
  const { state } = useLocation()
  const signatureFile = state && state.ownerSigFile ? state.ownerSigFile : undefined

  const [clientSecret, setClientSecret] = useState('')
  const [paymentAmount, setPaymentAmount] = useState('0')
  const [pirDetails, setPirDetails] = useState()
  const [display, setDisplay] = useState('carousel')

  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState()
  const [isComplete, setIsComplete] = useState()

  function handleBack() {
    const { pir, formattedAddress } = formatPIRForForm(pirDetails, signatureFile)
    navigate('/pir/create', { state: { pir, formattedAddress } })
  }

  function handlePayment() {
    if (pirDetails.ownerInformation.signatureSource.toLowerCase() === 'upload' && signatureFile) {
      setLoading(true)
      const isSignature = true
      const authorizer = 'owner'

      postUploadPIRFile(pirId, signatureFile, isSignature, authorizer)
        .catch(e => {
          console.error(`failed to upload the owner signature file for PIR with ID`, pirId, signatureFile, e)
        })
        .finally(() => {
          setLoading(false)
          setDisplay('confirmation')
        })
    } else {
      setDisplay('confirmation')
    }
  }

  useEffect(() => {
    function getPirDetails(pirId) {
      getPIRById(pirId)
        .then(({ data }) => {
          if (data.data.status !== 'NEW') {
            console.error('PIR already paid', pirId)
            setIsComplete(true)
          }
          setPirDetails(data.data)
          setLoading(false)
        })
        .catch(e => {
          console.error('failed to retrieve data for PIR', pirId, e)
          if (e.response && e.response.data.message === `pirId not found: ${pirId}`) {
            setErrorMessage([`The provided ${pirTerminology.getRequestName()} does not exist.`])
          } else {
            setErrorMessage([])
          }
          setLoading(false)
        })
    }

    if (pirId) getPirDetails(pirId)
  }, [pirId])

  useEffect(() => {
    function getPaymentIntent(pirId) {
      setLoading(true)
      getPaymentIntentSecret(pirId, 'PIR')
        .then(({ data }) => {
          setClientSecret(data.clientSecret)
          setPaymentAmount(data.amount)
          setLoading(false)
        })
        .catch(e => {
          console.error('failed to fetch payment intent secret', e)
          setErrorMessage(['Error getting payment details.', 'Please refresh the page to try again.'])
          setLoading(false)
        })
    }

    if (pirDetails && !isComplete) getPaymentIntent(pirId)
  }, [pirDetails, isComplete])

  if (loading) return <Loading />

  if (errorMessage !== undefined) {
    return <LoadingError message={errorMessage} />
  }

  if (isComplete) {
    return (
      <LoadingError message={[`This ${pirTerminology.getRequestName()} has already been paid for.`]}>
        <Button>
          <ReactLink to={`/pir/${pirId}/download`} target="_blank">
            Check {pirTerminology.getRequestShortName()} Status
          </ReactLink>
        </Button>
        <SmallBodyText>
          <Link to="/pir/create">Create a new {pirTerminology.getRequestShortName()}</Link>
        </SmallBodyText>
      </LoadingError>
    )
  }

  if (display === 'confirmation') {
    return (
      <>
        <FixedContainer>
          <Logo src={logo} />
        </FixedContainer>
        <PaymentConfirmation />
      </>
    )
  }

  return (
    <PageWithTitle gap="40px">
      <Header>
        <Logo src={logo}></Logo>
        <PageTitle>{pirTerminology.getRequestName()} Payment</PageTitle>
      </Header>
      <FlexColumn width="100%" gap="30px">
        <SpaceBetweenRowContainer gap="50px">
          <BackButton padding="large" onClick={handleBack}>
            <ArrowBackIosIcon />
            Back
          </BackButton>
          <Carousel steps={pirStepperArray} activeStep={2} />
        </SpaceBetweenRowContainer>
        <CenteredRowContainerReverse width="100%">
          {clientSecret && (
            <SectionContainer>
              <PaymentForm clientSecret={clientSecret} onSuccess={handlePayment} />
            </SectionContainer>
          )}
          <SectionContainer>
            <FlexColumn gap="15px">
              {pirDetails && <OrderSummary details={formatPIRForOrderSummary(pirDetails)} displayDetails={true} amount={paymentAmount} />}
            </FlexColumn>
          </SectionContainer>
        </CenteredRowContainerReverse>
      </FlexColumn>
    </PageWithTitle>
  )
}

PaymentPage.propTypes = {
  pirDetails: PropTypes.object
}

export default PaymentPage
